/**
 * TeamFormPage - Página de criação/edição de equipe
 *
 * Seção:
 * 1. Informações Básicas (nome, descrição)
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTeamData, useCreateTeam, useUpdateTeam } from '../hooks/useTeams';
import { Button } from '@/shared/components/ui/Button';
import { Breadcrumb, Input, Textarea } from '@/shared/components/ui';
import { TeamMembersSection } from '../components/sections/TeamMembersSection';
import type { CreateTeamRequest, UpdateTeamRequest } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
export const TeamFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // Fetch team data (only in edit mode)
  const { data: team, isLoading, isError } = useTeamData(id);
  const createMutation = useCreateTeam();
  const updateMutation = useUpdateTeam();

  // Form state
  const [formData, setFormData] = useState<Partial<CreateTeamRequest & UpdateTeamRequest>>({
    name: '',
    description: '',
  });
  const [hasChanges, setHasChanges] = useState(!isEditMode); // New teams always have changes

  // Update form data when team loads (edit mode)
  React.useEffect(() => {
    if (team) {
      setFormData({
        name: team.name,
        description: team.description,
      });
      setHasChanges(false);
    }
  }, [team]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Validation
  const validateForm = (): string | null => {
    if (!formData.name?.trim()) return 'Nome é obrigatório';
    if (formData.name.trim().length < 2) return 'Nome deve ter pelo menos 2 caracteres';

    return null;
  };

  // Save changes
  const handleSave = async () => {
    if (!hasChanges) return;

    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      if (isEditMode) {
        // Update existing team
        await updateMutation.mutateAsync({
          id: id!,
          data: formData as UpdateTeamRequest
        });
        setHasChanges(false);
        navigate('/settings/teams');
      } else {
        // Create new team
        await createMutation.mutateAsync(formData as CreateTeamRequest);
        navigate('/settings/teams');
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar equipe:', error);
      alert('Erro ao salvar equipe. Tente novamente.');
    }
  };

  // Loading state (only for edit mode)
  if (isEditMode && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-button-primary)' }} />
      </div>
    );
  }

  // Error state (only for edit mode)
  if (isEditMode && (isError || !team)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-lg" style={{ color: 'var(--theme-error-color)' }}>
          Erro ao carregar equipe
        </p>
        <Button onClick={() => window.location.reload()} variant="secondary">
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <div className="px-4 h-14 flex flex-col justify-center">
          <div className="flex items-center justify-between">
            {/* Breadcrumb (F25-31: shared — mobile = legenda condensada sob o título, NUNCA seta voltar) */}
            <Breadcrumb
              items={[
                { label: 'Configurações', onNavigate: () => navigate('/settings'), testid: 'teams-form-breadcrumb-settings' },
                { label: 'Equipes', onNavigate: () => navigate('/settings/teams'), testid: 'teams-form-breadcrumb' },
                { label: isEditMode ? (team ? team.name : 'Editar equipe') : 'Nova equipe', testid: 'teams-form-breadcrumb-current' },
              ]}
              mobileLegend="Configurações · Equipe"
              data-testid="teams-form-breadcrumb-nav"
            />

            <div className="flex items-center gap-3">
              {hasChanges && (
                <span
                  className="text-sm hidden sm:inline"
                  style={{ color: 'var(--theme-warning-color)' }}
                >
                  {isEditMode ? 'Alterações não salvas' : 'Preencha os campos obrigatórios'}
                </span>
              )}
              <Button
                variant="secondary"
                className="hidden sm:inline-flex"
                onClick={() => navigate('/settings/teams')}
                data-testid="teams-form-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <>
                    <Icon name="loader" className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode ? 'Salvando...' : 'Criando...'}
                  </>
                ) : (
                  <>
                    <Icon name="save" className="w-4 h-4 mr-2" />
                    {isEditMode ? 'Salvar Alterações' : 'Criar Equipe'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="space-y-6">
          {/* Basic Info Section */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div
              className="flex items-start gap-3 px-[18px] py-[15px] border-b"
              style={{ borderColor: 'var(--theme-card-border)' }}
            >
              <div
                className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: 'var(--theme-primary-background)',
                  color: 'var(--theme-primary)',
                }}
              >
                <Icon name="users" className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold" style={{ color: 'var(--theme-card-text)' }}>
                  Informações Básicas
                </h2>
                <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                  Nome e descrição da equipe
                </p>
              </div>
            </div>

            <div className="space-y-4 p-[18px]">
              {/* Nome */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Nome da Equipe *
                </label>
                <Input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="Ex: Suporte, Vendas, Marketing"
                  data-testid="team-form-name"
                />
              </div>

              {/* Descrição */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Descrição
                </label>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Descreva as responsabilidades desta equipe"
                  rows={3}
                  resize="none"
                  data-testid="team-form-description"
                />
              </div>
            </div>
          </div>

          {/* Team Members Section - Only in edit mode */}
          {isEditMode && id ? (
            <TeamMembersSection teamId={id} />
          ) : (
            /* mockup: nota explicativa no create (.note solta, sem card) */
            <div className="flex items-center gap-2 px-1">
              <Icon name="info" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
              <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                Os membros são adicionados após a criação da equipe.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TeamFormPage.displayName = 'TeamFormPage';
